import { Package, TrendingUp, ShoppingCart, Settings, RefreshCw, Rocket } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../ui/utils";

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const navItems = [
  { id: 'inventaire', label: 'Inventaire', icon: Package },
  { id: 'commande', label: 'Commande', icon: ShoppingCart },
  { id: 'ventes', label: 'Ventes', icon: TrendingUp },
];

const secondaryNavItems = [
  // Onboarding lives next to the other setup screens: it is used heavily for a
  // few days when an establishment starts, then rarely.
  { id: 'demarrage', label: 'Démarrage', icon: Rocket },
  { id: 'synchronisation', label: 'Sync', icon: RefreshCw },
  { id: 'parametres', label: 'Paramètres', icon: Settings },
];

export function Sidebar({ activeTab, onTabChange }: SidebarProps) {
  const location = useLocation();

  const isActive = (itemId: string) => {
    if (location.pathname === '/' && itemId === 'inventaire') {
      return true;
    }
    return location.pathname === `/${itemId}`;
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:block sticky top-16 self-start w-16 lg:w-20 mr-6 lg:mr-8 mt-8">
        <div className="bg-[var(--sidebar-background)] border border-[var(--sidebar-border)] rounded-sidebar shadow-sidebar flex flex-col">
          <div className="flex flex-col items-center py-2.5 gap-0.5">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={`/${item.id}`}
                className={cn(
                  'w-full px-2.5 lg:px-3 py-3 lg:py-3.5 flex flex-col items-center gap-1.5 lg:gap-2 transition-all duration-200 rounded-sidebar-item group relative',
                  isActive(item.id)
                    ? 'bg-[var(--color-steel-dark)]/30 text-[var(--color-steel)] shadow-[0_0_12px_rgba(122,168,200,0.15)]'
                    : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] hover:shadow-[0_0_8px_rgba(122,168,200,0.08)]'
                )}
              >
                {isActive(item.id) && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 bg-[var(--color-steel)] rounded-r-full shadow-[0_0_8px_rgba(122,168,200,0.4)]" />
                )}
                <item.icon className={`w-6 h-6 icon-hover transition-all duration-200 ${isActive(item.id) ? 'scale-110' : 'group-hover:scale-105'}`} />
              </Link>
            ))}
          </div>

          {/* Separator */}
          <div className="mx-3 border-t border-[var(--border)]" />

          {/* Secondary nav items */}
          <div className="flex flex-col items-center py-2.5 gap-0.5">
            {secondaryNavItems.map((item) => (
              <Link
                key={item.id}
                to={`/${item.id}`}
                className={cn(
                  'w-full px-2.5 lg:px-3 py-2.5 lg:py-3 flex flex-col items-center gap-1.5 lg:gap-2 transition-all duration-200 rounded-sidebar-item group relative',
                  isActive(item.id)
                    ? 'bg-[var(--color-steel-dark)]/30 text-[var(--color-steel)] shadow-[0_0_12px_rgba(122,168,200,0.15)]'
                    : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] hover:shadow-[0_0_8px_rgba(122,168,200,0.08)]'
                )}
              >
                {isActive(item.id) && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-[var(--color-steel)] rounded-r-full shadow-[0_0_8px_rgba(122,168,200,0.4)]" />
                )}
                <item.icon className={`w-5 h-5 icon-hover transition-all duration-200 ${isActive(item.id) ? 'scale-110' : 'group-hover:scale-105'}`} />
              </Link>
            ))}
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[var(--background)] border-t border-[var(--border)] z-40 safe-area-bottom">
        <div className="flex items-center justify-around px-2 pb-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`
                  relative flex flex-col items-center gap-1 py-2 px-4 rounded-lg
                  transition-all duration-200 flex-1 max-w-[100px]
                  ${active
                    ? 'text-[var(--color-steel)]'
                    : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)] active:bg-[var(--muted)]'
                  }
                `}
              >
                {active && (
                  <div className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-12 h-1 bg-[var(--color-steel)] rounded-b-full" />
                )}
                <Icon className={`w-6 h-6 transition-transform ${active ? 'scale-110' : ''}`} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
